<template>
  <v-card
    class="pie-container"
    :class="this.legendType ? this.legendType.toString() : ''"
    ref="chartContainer"
  >
    <!-- Unprovisioned machine title -->
    <div v-if="this.disabled" class="text-center mb-0 disabled">
      <p class="pb-0 mb-0 text-uppercase text-h6 two-lines-text-truncate">
        {{ title }}
      </p>
    </div>

    <!-- Provisioned machine title -->
    <div v-else class="bg-secondary text-center">
      <p class="text-uppercase text-h6">{{ title }}</p>
    </div>

    <div class="chart-container">
      <div
        class="d-flex align-center justify-center ma-0 pa-0 px-sm-7 overflow-y-auto overflow-x-hidden"
        style="
          position: absolute;
          width: 100%;
          top: 2em;
          left: 0;
          bottom: 0;
          overflow-y: hidden !important;
        "
      >
        <apexchart
          type="pie"
          :width="'100%'"
          :height="apexChartHeight"
          :options="chartOptions"
          :series="series"
        />
      </div>
    </div>

    <v-overlay
      v-if="this.disabled"
      absolute
      color="#fff"
      opacity=".9"
      class="text-high-emphasis"
    >
      <h1 class="text-h6 d-flex mb-5">
        <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
        {{ $t("machines.featureNotAvailable") }}
      </h1>
    </v-overlay>
    <div v-if="isFinite(total)" class="total">
      {{ $t("common.total") }}: {{ total }}
    </div>
  </v-card>
</template>
<script>
import VueApexCharts from "vue3-apexcharts"
import colors from "vuetify/lib/util/colors"

export default {
  name: "Pie",
  props: {
    title: String,
    metricsData: Object,
    disabled: Boolean,
    formatHours: Boolean,
    total: Number,
    legendFormatter: { type: Function, default: (seriesName) => seriesName },
    legendType: {
      validator(value) {
        return ["vertical", "horizontal"].includes(value)
      },
      default: "vertical",
    },
  },
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      apexChartWidth: 400,
      apexChartHeight: null,
      lastContainerWidth: 0,
    }
  },
  // bind event handlers to the `handleResize` method (defined below)
  mounted: function () {
    window.addEventListener("resize", this.handleResize)
    // <apexchart> sets up its chart instance asynchronously after mount.
    // Resizing before that finishes crashes it, since it tries to destroy
    // a chart instance that isn't there yet.
    this.$nextTick(() => this.handleResize())
  },
  beforeUnmount: function () {
    window.removeEventListener("resize", this.handleResize)
  },
  methods: {
    handleResize() {
      let containerWidth = this.$refs.chartContainer.$el.clientWidth

      if (Math.abs(containerWidth - this.lastContainerWidth) > 100) {
        if (containerWidth > 800) {
          containerWidth = 800
        }
        this.apexChartWidth = containerWidth - 25
        this.lastContainerWidth = containerWidth
      }

      if (!this.apexChartHeight) {
        this.apexChartHeight = this.$refs.chartContainer.$el.clientHeight - 40
      }
    },
  },
  computed: {
    series() {
      return this.metricsData.dataSet
    },
    chartOptions() {
      return {
        chart: {
          offsetX: 0,
          fontFamily: "Roboto, sans-serif",
        },
        noData: {
          text: this.$t("$vuetify.noDataText"),
          style: {
            fontFamily: "Roboto, sans-serif",
          },
        },
        tooltip: {
          y: this.formatHours
            ? {
                formatter: function (value) {
                  return `${Math.floor(value / 3600)} h`
                },
              }
            : {},
        },
        colors: [
          colors.orange.darken1,
          colors.green.lighten2,
          colors.blueGrey.darken3,
          colors.amber.lighten2,
        ],
        dataLabels: {
          enabled: true,
          dropShadow: false,
          formatter: function (val) {
            return val.toFixed(0) + " %"
          },
          style: {
            fontFamily: "Roboto, sans-serif",
            fontWeight: 300,
          },
        },
        legend: {
          show: this.series.length > 0,
          position: "right",
          // Cap at 2/3 of the widget width so the pie always keeps 1/3,
          // without a width the legend defaults to one narrow column.
          width:
            this.legendType === "horizontal"
              ? Math.max(Math.round((this.apexChartWidth * 2) / 3), 200)
              : undefined,
          markers: {
            // Plain div (not apex's fixed-size SVG marker) so it can
            // stretch full-width; currentColor picks up apex's per-series
            // color, set on this span before the HTML is injected.
            customHTML:
              this.legendType === "horizontal"
                ? () => "<div class=\"legend-color-line\"></div>"
                : undefined,
          },
          formatter: this.legendFormatter,
        },
        plotOptions: {
          pie: {
            offsetY: 0,
            customScale: 0.7,
            dataLabels: {
              offset: -15,
            },
            donut: {
              size: "50%",
            },
          },
        },
        labels: this.metricsData.labels,
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@mixin fit-parent-size {
  width: 100% !important;
}

.pie-container {
  --pie-reserved-width: 170;
  --pie-legend-min-width: 5.5rem;
  --pie-legend-shadow: 0px 3px 3px -2px rgba(0, 0, 0, 0.2),
    0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12) !important; //.elevation-3
  min-height: 250px; //When changin adjust: "customScale"
  position: relative;
  overflow: hidden;
  height: 100%;
}
.pie-container :deep(.chart-container) {
  @include fit-parent-size;
  & > div {
    @include fit-parent-size;
    & > div {
      @include fit-parent-size;
      & > div {
        @include fit-parent-size;
        & > svg {
          @include fit-parent-size;
          width: 100% !important;
          height: 100% !important;
          flex-direction: row;
          & > foreignObject {
            width: 100% !important;
            height: 100% !important;
          }
        }
      }
    }
  }
}
.pie-container.horizontal {
  min-width: 360px;
}

.pie-container.vertical {
  min-width: 360px;
}

.pie-container :deep(foreignObject) {
  display: block;
}

.pie-container :deep(.total) {
  position: absolute;
  left: 10px;
  bottom: 5px;
  z-index: 100;
  font-weight: 800;
}

.pie-container :deep(.apexcharts-legend) {
  // Let ApexCharts position/size this natively; forcing it independently
  // previously misaligned the legend next to the pie.
  align-content: start;
  justify-content: center;
}

.pie-container.horizontal :deep(.apexcharts-legend) {
  display: grid;
  align-content: space-evenly;

  grid-template-columns: repeat(
    auto-fit,
    minmax(var(--pie-legend-min-width), auto)
  );
  grid-gap: 0rem;
  // Never bleed past the box horizontally (overlaps the pie); scroll
  // vertically instead of clipping cards that don't fit.
  overflow-x: hidden;
  overflow-y: auto;
  // display: flex;
  // flex-direction: row !important;
  // flex-flow: wrap;
}

.pie-container.vertical :deep(.apexcharts-legend) {
  display: grid;
  align-content: center;
  // Same as the horizontal legend above: contain horizontally, scroll
  // vertically.
  overflow-x: hidden;
  overflow-y: auto;
  // display: flex;
  // flex-direction: column;
  // flex-wrap: wrap;
  // align-content: center;
  // justify-content: center;
  // min-width: var(--pie-legend-min-width);
  //max-width: calc(var(--pie-legend-min-width) + 100px);
}

.pie-container.vertical :deep(.apexcharts-legend-series) {
  min-width: var(--pie-legend-min-width);
  flex-shrink: 0;
}

.pie-container.horizontal :deep(.apexcharts-legend-series) {
  margin: 2px !important;
  flex-grow: 1;
  text-align: left;
  padding: 2px !important;
  -webkit-box-shadow: var(--pie-legend-shadow);
  box-shadow: var(--pie-legend-shadow);
  border-radius: 4px;
  display: block;
  position: relative;
  place-items: center;
  place-content: center;
  // Default grid min-width is content size, which can force the track (and
  // legend) wider than its box; allow shrinking below that.
  min-width: 0;
}

.pie-container :deep(.apexcharts-legend-text) {
  min-width: var(--pie-legend-min-width);
}

.pie-container.horizontal :deep(.apexcharts-legend-text) {
  margin: 5px -2px;
  padding-left: 2px;
  padding-right: 2px;
  padding-top: 2px;
  border-radius: 0px;
  display: inline-block;
  height: calc(100% - 10px);
  min-width: 2rem;
  white-space: nowrap;
  width: calc(100% + 4px);
  background-color: rgba(255, 255, 255, 0.5);
}

.pie-container.horizontal :deep(.apexcharts-legend-marker) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 6px !important;
}

.pie-container :deep(.legend-color-line) {
  width: 100%;
  height: 100%;
  background-color: currentColor;
}

.pie-container.vertical :deep(.apexcharts-legend-marker) {
  min-width: 24px !important;
}
</style>
